import { config } from "@/config";
import { clearExpiresAt, storeExpiresAt } from "@/features/auth/lib/token";
import { useAuth } from "@/features/auth/hooks/useAuth";
import type { TenantPermissions } from "@/features/auth/lib/api";
import { API_REQUEST_TIMEOUT_MS } from "@/lib/query-constants";

interface RefreshResponse {
  success: boolean;
  data: {
    token: string;
    refreshToken: string;
    expiresIn: number;
    expiresAt: number;
    csrfToken: string;
    tenants: TenantPermissions[];
    /** The user's access-group IDs. Optional only while an older API pod may answer mid-rollout. */
    accessIds?: string[];
    /** Canonical avatar URL resolved by the backend (uploaded avatar → signed URL, else provider photo). Absent on legacy responses. */
    picture?: string | null;
  };
}

// Mutex: at most one refresh request in flight
let refreshPromise: Promise<RefreshResponse["data"]> | null = null;

/**
 * Silently refresh the Nexus JWT using the HttpOnly refresh cookie.
 * Concurrent callers share the same in-flight request (mutex).
 *
 * On success: updates expiresAt in localStorage, csrfToken + tenants in Zustand.
 * On failure: throws — caller decides whether to redirect to /login.
 */
export async function refreshNexusToken(): Promise<RefreshResponse["data"]> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = doRefresh();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function doRefresh(): Promise<RefreshResponse["data"]> {
  let res: Response;
  try {
    res = await fetch(`${config.authUrl}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({}),
      signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
    });
  } catch (err) {
    clearExpiresAt();
    throw err;
  }

  if (!res.ok) {
    clearExpiresAt();
    const body = (await res.json().catch(() => null)) as {
      message?: string;
    } | null;
    throw new Error(body?.message ?? `Refresh failed: ${res.status}`);
  }

  const json = (await res.json()) as RefreshResponse;
  const data = json.data;

  storeExpiresAt(data.expiresAt);

  const auth = useAuth.getState();
  auth.updateSession(data.expiresAt, data.csrfToken, data.tenants);

  // Sync client-side user.acs so self-edit checks (e.g. PersonDrawer) reflect the new grant/revoke.
  // An absent field (older API pod mid-rollout) leaves it as-is; `[]` is authoritative.
  if (data.accessIds !== undefined) auth.updateUserAcs(data.accessIds);

  // Keep the menu avatar fresh — re-resolves the signed URL (which expires) and
  // picks up an avatar uploaded in another session. `null` is authoritative
  // (user has no avatar); only an absent field (legacy response) leaves it as-is.
  if (data.picture !== undefined) auth.updateUserPicture(data.picture);

  return data;
}
